import { Expose, Transform, Type } from 'class-transformer'
import { WriterEnum } from '@interfaces/WriterEnum'
import { AffiliationEnum } from '@interfaces/AffiliationEnum'
import { ResidenceEnum } from '@interfaces/ResidenceEnum'
import { CertificationTaxEnum } from '@interfaces/CertificationTaxEnum'
import { BankAccountType } from '@models/writer/BankAccountType'
import type { ImageType } from '@interfaces/ImageType'
import { WritersConsentType } from './WritersConsentType'

export class WriterFormType {
  @Expose()
  id?: number

  @Expose()
  type: WriterEnum

  @Expose()
  affiliation: AffiliationEnum

  @Expose()
  fullName: string

  @Expose()
  idNumber: string

  @Expose()
  email: string

  @Expose()
  phoneNumber: string

  @Expose()
  residence: ResidenceEnum

  @Expose()
  country: string

  @Expose()
  @Transform(
    ({ value, obj }) => (obj.residence === ResidenceEnum.LOCAL ? value : ''),
    { toClassOnly: true }
  )
  address: string

  @Expose()
  @Transform(
    ({ value, obj }) => (obj.residence === ResidenceEnum.LOCAL ? value : ''),
    { toClassOnly: true }
  )
  postcode: string

  @Expose()
  province: string

  @Expose()
  district: string

  @Expose()
  subDistrict: string

  @Expose()
  @Transform(
    ({ value, obj }) => {
      if (
        obj.type === WriterEnum.INDIVIDUAL &&
        obj.residence === ResidenceEnum.LOCAL
      ) {
        return {
          url: value,
          name: typeof value === 'string' ? value.split('/').pop() : '',
        }
      }

      return { url: '', name: '' }
    },
    {
      toClassOnly: true,
    }
  )
  idCardImgPath: ImageType

  @Expose()
  isSameMainAddress: boolean

  @Expose()
  sendResidence: ResidenceEnum

  @Expose()
  sendCountry: string

  @Expose()
  @Transform(
    ({ value, obj }) =>
      obj.sendResidence === ResidenceEnum.LOCAL && !obj.isSameMainAddress
        ? value
        : '',
    { toClassOnly: true }
  )
  sendAddress: string

  @Expose()
  @Transform(
    ({ value, obj }) =>
      obj.sendResidence === ResidenceEnum.LOCAL && !obj.isSameMainAddress
        ? value
        : '',
    { toClassOnly: true }
  )
  sendPostcode: string

  @Expose()
  sendProvince: string

  @Expose()
  sendDistrict: string

  @Expose()
  sendSubDistrict: string

  @Expose()
  certificationTax: CertificationTaxEnum

  @Expose()
  companyName: string

  @Expose()
  taxId: string

  @Expose()
  @Transform(
    ({ value }) => ({
      url: value,
      name: typeof value === 'string' ? value.split('/').pop() : '',
    }),
    {
      toClassOnly: true,
    }
  )
  companyCertificateImgPath: ImageType

  @Expose()
  @Transform(
    ({ value }) => ({
      url: value,
      name: typeof value === 'string' ? value.split('/').pop() : '',
    }),
    {
      toClassOnly: true,
    }
  )
  vatRegistrationCertificateImgPath: ImageType

  @Expose()
  coordinatorFullName: string

  @Expose()
  coordinatorEmail: string

  @Expose()
  coordinatorPhoneNumber: string

  @Expose()
  accountingFullName: string

  @Expose()
  accountingEmail: string

  @Expose()
  accountingPhoneNumber: string

  @Expose()
  certificateCountry: string

  @Expose()
  certificateAddress: string

  @Expose()
  certificatePostcode: string

  @Expose()
  certificateProvince: string

  @Expose()
  certificateDistrict: string

  @Expose()
  certificateSubDistrict: string

  @Expose({ name: 'writersConsents' })
  @Transform(
    ({ value }) => {
      return value
        ? value.map((item: WritersConsentType) => ({
            id: item.consent.id,
            checked: true,
          }))
        : []
    },
    { toClassOnly: true }
  )
  consents: { id: number; checked: boolean }[]

  @Expose()
  @Transform(
    ({ value }) => {
      if (value) {
        return {
          ...value,
          bankCoverImgPath: {
            name: value.bankCoverImgPath?.split('/').pop(),
            url: value.bankCoverImgPath,
          },
        }
      }

      return {}
    },
    { toClassOnly: true }
  )
  bankAccount: BankAccountType

  @Expose()
  @Transform(
    ({ obj }) => (obj.residence === ResidenceEnum.ABOARD ? obj.address : ''),
    { toClassOnly: true }
  )
  aboardAddress: string

  @Expose()
  @Transform(
    ({ obj }) => (obj.residence === ResidenceEnum.ABOARD ? obj.postcode : ''),
    { toClassOnly: true }
  )
  aboardPostcode: string

  @Expose()
  @Transform(
    ({ obj }) => {
      if (
        obj.type === WriterEnum.INDIVIDUAL &&
        obj.residence === ResidenceEnum.ABOARD
      ) {
        return {
          url: obj.idCardImgPath,
          name:
            typeof obj.idCardImgPath === 'string'
              ? obj.idCardImgPath?.split('/').pop()
              : '',
        }
      }
      return { url: '', name: '' }
    },
    {
      toClassOnly: true,
    }
  )
  aboardIdCardImgPath: ImageType

  @Expose()
  @Transform(
    ({ obj }) =>
      obj.type === WriterEnum.LEGAL && obj.residence === ResidenceEnum.ABOARD
        ? obj.certificateAddress
        : '',
    { toClassOnly: true }
  )
  aboardCertificateAddress: string

  @Expose()
  @Transform(
    ({ obj }) =>
      obj.type === WriterEnum.LEGAL && obj.residence === ResidenceEnum.ABOARD
        ? obj.certificatePostcode
        : '',
    { toClassOnly: true }
  )
  aboardCertificatePostcode: string

  @Expose()
  @Transform(
    ({ obj }) =>
      obj.sendResidence === ResidenceEnum.ABOARD && !obj.isSameMainAddress
        ? obj.sendAddress
        : '',
    { toClassOnly: true }
  )
  aboardSendAddress: string

  @Expose()
  @Transform(
    ({ obj }) =>
      obj.sendResidence === ResidenceEnum.ABOARD && !obj.isSameMainAddress
        ? obj.sendPostcode
        : '',
    { toClassOnly: true }
  )
  aboardSendPostcode: string

  validateNumberId: boolean

  validateTaxId: boolean
}
